import { preferenceOptions } from "constant";
import { serviceInstanceOptions } from "constant";
import { excelAndDbClientFieldsMapping } from "constant";
import { paymentOptions } from "constant";
import { leadOptions } from "constant";
import Joi from "joi";
import moment from "moment";
import { getUserLink } from "utils/helper";

const normalizeText = (value) =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ") : value;

const isBlankCell = (value) =>
  value === undefined || value === null || normalizeText(value) === "";

const normalizeHeader = (header) =>
  header?.toString().trim().replace(/\s*\*+$/, "");

const getOptionLookupKey = (value) =>
  normalizeText(value)?.toString().toLowerCase().replace(/[\s_-]+/g, "");

const getCanonicalOptionValue = (value, options, aliases = {}) => {
  const normalizedKey = getOptionLookupKey(value);
  if (!normalizedKey) {
    return value;
  }

  const aliasValue = aliases[normalizedKey];
  if (aliasValue) {
    return aliasValue;
  }

  const matchedOption = options.find(
    (option) => getOptionLookupKey(option.value) === normalizedKey
  );
  return matchedOption?.value ?? normalizeText(value);
};

const getAllowedOptionValues = (options) =>
  options.map((option) => option.value).join(", ");

const roundCurrency = (value) => Math.round((parseFloat(value) || 0) * 100) / 100;

const getExcelColumnIndex = (headers, mapping) => {
  const acceptedHeaders = [mapping.excelName, ...(mapping.aliases ?? [])];
  return headers.findIndex((header) =>
    acceptedHeaders.includes(normalizeHeader(header))
  );
};

function ExcelDateToJSDate(serial) {
  if (!serial) return undefined;
  if (serial instanceof Date) {
    return Number.isNaN(serial.getTime()) ? undefined : serial;
  }
  if (typeof serial === "number") {
    let utc_days = Math.floor(serial - 25569);
    let utc_value = utc_days * 86400;
    return new Date(utc_value * 1000);
  }
  if (typeof serial === "string") {
    let parts = serial.split("/");
    return new Date(parts[2], parts[0] - 1, parts[1]);
  }
  return undefined;
}

const safeFormatDate = (val) => {
  if (!val) {
    return undefined;
  }
  const jsDate = ExcelDateToJSDate(val);
  if (!jsDate || isNaN(jsDate.getTime())) {
    return undefined;
  }
  return moment(jsDate).format("MM/DD/YYYY");
};

const mapServicesToObject = (services) => {
  const serviceObject = {};
  services.forEach((item) => {
    serviceObject[item.label.toLowerCase()] = {
      id: item.id,
      name: item.value.toLowerCase(),
      totalAmount: item.totalAmount,
    };
  });
  return serviceObject;
};

const mapClientsToObject = (clients, dbPhoneNumberObject, dbEmailObject) => {
  const clientObject = {};
  clients.forEach((item) => {
    const key = `${item.name}_${item.phoneNumber}`.toLowerCase();
    clientObject[key] = true;
    dbPhoneNumberObject[item.phoneNumber] = true;
    dbEmailObject[item.email] = true;
  });
  return clientObject;
};

const phoneRegex = /^[0-9]{10}$/;

const clientSchema = Joi.object({
  clientName: Joi.string().required().min(2).max(100).label("Client Name"),
  phoneNumber: Joi.number().required().label("Phone Number"),
  email: Joi.string().email({ tlds: false }).allow("", null).optional().label("Email"),
  address: Joi.string().max(255).required().label("Address"),
  occupation: Joi.string().max(100).required().label("Occupation"),
  package: Joi.string().required().label("Choose Package"),
  serviceInstance: Joi.string()
    .valid(...serviceInstanceOptions.map((item) => item.value))
    .required()
    .label("Service Instance")
    .insensitive(),
  startDate: Joi.date().required().label("Start Date"),
  completionDate: Joi.date().required().label("Completion Date"),

  familyName: Joi.string().min(2).max(100).label("Family Name"),
  gender: Joi.string()
    .valid("Male", "Female", "Other")
    .required()
    .label("Gender")
    .insensitive(),
  leadFrom: Joi.string()
    .valid(...leadOptions.map((item) => item.value))
    .required()
    .label("Lead From")
    .insensitive(),
  preference: Joi.string()
    .valid(...preferenceOptions.map((item) => item.value))
    .required()
    .label("Food Preference")
    .insensitive(),
  referredBy: Joi.string().max(100).label("Referred By"),
  discount: Joi.number().integer().min(0).optional().label("Discount Amount"),
  amountPaid: Joi.number().min(0).required().label("Amount Paid"),
  paymentMode: Joi.string()
    .valid(...paymentOptions.map((item) => item.value))
    .required()
    .label("Payment Mode")
    .insensitive(),
  dob: Joi.date().label("Date of Birth"),
  anniversary: Joi.date().label("Anniversary"),
})
  .messages({
    "string.base": "{{#label}} must be a string",
    "string.empty": "{{#label}} cannot be empty",
    "string.min": "{{#label}} should have at least {{#limit}} characters",
    "string.max": "{{#label}} should not exceed {{#limit}} characters",
    "string.email": "{{#label}} must be a valid email address",
    "string.pattern.base": "{{#label}} must be a 10-digit number",
    "number.base": "{{#label}} must be a number",
    "number.integer": "{{#label}} must be an integer",
    "number.min": "{{#label}} should not be less than {{#limit}}",
    "date.format": "{{#label}} must be in the format MM/DD/YYYY",
    "date.base": "{{#label}} must be a valid MM/DD/YYYY format",
    "any.only": "{{#label}} is not a valid value",
  })
  .unknown();

export const validateClient = async (
  dataArray,
  clientArray,
  serviceOptions,
  adminId
) => {
  const extractedData = [];
  const errors = [];
  const dbPhoneNumberObject = {};
  const dbEmailObject = {};

  const serviceObject = mapServicesToObject(serviceOptions);

  const clientObject = mapClientsToObject(
    clientArray,
    dbPhoneNumberObject,
    dbEmailObject
  );

  let customerCount = 0;
  clientArray.forEach((doc) => {
    let cusId = doc.customerId;
    if (cusId) {
      if (cusId > customerCount) {
        customerCount = cusId;
      }
    }
  });

  for (let i = 1; i < dataArray.length; i++) {
    const dataRow = dataArray[i];

    const clientData = {
      createdAt: new Date(),
      adminId,
      customerId: customerCount + i,
      step: [1, 1, 1],
    };

    const excelClientObject = {};
    const phoneNumberObject = {};
    const emailObject = {};

    for (const mapping of excelAndDbClientFieldsMapping) {
      const columnIndex = getExcelColumnIndex(dataArray[0], mapping);
      if (columnIndex !== -1) {
        clientData[mapping.dbField] = normalizeText(dataRow[columnIndex]);
      }
    }

    clientData.serviceInstance = getCanonicalOptionValue(
      clientData.serviceInstance,
      serviceInstanceOptions
    );
    clientData.preference = getCanonicalOptionValue(
      clientData.preference,
      preferenceOptions,
      {
        nonveg: "Non-veg",
        nonvegetarian: "Non-veg",
        eggitarian: "Eggetarian",
      }
    );
    clientData.leadFrom = getCanonicalOptionValue(
      clientData.leadFrom,
      leadOptions,
      {
        google: "Google Ads",
      }
    );
    clientData.paymentMode = getCanonicalOptionValue(
      clientData.paymentMode,
      paymentOptions
    );

    clientData.dob = safeFormatDate(clientData.dob);
    clientData.anniversary = safeFormatDate(clientData.anniversary);
    clientData.startDate = safeFormatDate(clientData.startDate);
    clientData.completionDate = safeFormatDate(clientData.completionDate);

    let parsedDiscount = isBlankCell(clientData.discount)
      ? undefined
      : parseFloat(clientData.discount) || 0;
    // Excel percentage-formatted cells store values as decimals (e.g. 10% → 0.10).
    // If the value is between 0 and 1 (exclusive), treat it as a percentage and convert.
    if (parsedDiscount > 0 && parsedDiscount < 1) {
      parsedDiscount = Math.round(parsedDiscount * 100);
    }
    clientData.discount = parsedDiscount;
    clientData.amountPaid = isBlankCell(clientData.amountPaid)
      ? undefined
      : parseFloat(clientData.amountPaid) || 0;

    try {
      const validatedClientData = await clientSchema.validateAsync(clientData, {
        abortEarly: false,
        stripUnknown: false,
        convert: true,
      });
      Object.assign(clientData, validatedClientData);

      clientData.dob = safeFormatDate(clientData.dob);
      clientData.anniversary = safeFormatDate(clientData.anniversary);
      clientData.startDate = safeFormatDate(clientData.startDate);
      clientData.completionDate = safeFormatDate(clientData.completionDate);

      if (clientData.dob) {
        const dob = moment(clientData.dob, "MM/DD/YYYY");
        if (!dob.isValid()) {
          errors.push({
            row: i + 1,
            field: "Date of Birth",
            message: `Invalid date of birth, ${clientData.dob} is not a valid date.`,
          });
        } else if (!dob.isBefore(moment())) {
          errors.push({
            row: i + 1,
            field: "Date of Birth",
            message: `Invalid date of birth, ${clientData.dob} should be in past.`,
          });
        } else {
          clientData.age = moment().diff(dob, "years");
        }
      }

      if (clientData.anniversary) {
        const anniversary = moment(clientData.anniversary, "MM/DD/YYYY");
        if (!anniversary.isValid()) {
          errors.push({
            row: i + 1,
            field: "Anniversary",
            message: `Invalid anniversary, ${clientData.anniversary} is not a valid date.`,
          });
        }
      }

      const startDate = moment(clientData.startDate, "MM/DD/YYYY");
      if (!startDate.isValid()) {
        errors.push({
          row: i + 1,
          field: "Start Date",
          message: `Invalid start date, ${clientData.startDate} is not a valid date.`,
        });
      }

      const completionDate = moment(clientData.completionDate, "MM/DD/YYYY");
      if (!completionDate.isValid()) {
        errors.push({
          row: i + 1,
          field: "Completion Date",
          message: `Invalid completion date, ${clientData.completionDate} is not a valid date.`,
        });
      }

      if (
        startDate.isValid() &&
        completionDate.isValid() &&
        !completionDate.isAfter(startDate)
      ) {
        errors.push({
          row: i + 1,
          field: "Completion Date",
          message: `Invalid completion date, ${clientData.completionDate} should be greater than start date.`,
        });
      }

      const isDuplicate =
        excelClientObject[
          `${clientData.clientName}_${clientData.phoneNumber}`?.toLowerCase()
        ];
      if (isDuplicate) {
        errors.push({
          row: i + 1,
          field: "Client Name / Phone Number",
          message: `${clientData.clientName}/${clientData.phoneNumber}, Duplicate client (name & phone number) is not allowed.`,
        });
      }

      const existingClient =
        clientObject[
          `${clientData.clientName}_${clientData.phoneNumber}`?.toLowerCase()
        ];
      if (existingClient) {
        errors.push({
          row: i + 1,
          field: "Client Name / Phone Number",
          message: `${clientData.clientName}/${clientData.phoneNumber}, Already exists.`,
        });
      }

      const isPackageValid = serviceObject.hasOwnProperty(
        clientData.package?.toLowerCase()
      );
      if (!isPackageValid) {
        errors.push({
          row: i + 1,
          field: "Choose Package",
          message: `Invalid package, ${
            clientData.package
          } does not exist. Please choose one of: ${getAllowedOptionValues(
            serviceOptions
          )}.`,
        });
      }

      const isPhoneNumberValid = phoneRegex.test(clientData.phoneNumber);
      if (!isPhoneNumberValid) {
        errors.push({
          row: i + 1,
          field: "Phone Number",
          message: `Invalid phone number, ${clientData.phoneNumber} is not valid.`,
        });
      }

      if (phoneNumberObject[clientData.phoneNumber]) {
        errors.push({
          row: i + 1,
          field: "Phone Number",
          message: `Duplicate phone number, ${clientData.phoneNumber} already exists in your excel.`,
        });
      }
      if (dbPhoneNumberObject[clientData.phoneNumber]) {
        errors.push({
          row: i + 1,
          field: "Phone Number",
          message: `Duplicate phone number, ${clientData.phoneNumber} already exists in our database.`,
        });
      }
      if (clientData.email && dbEmailObject[clientData.email]) {
        errors.push({
          row: i + 1,
          field: "Email",
          message: `Duplicate email, ${clientData.email} already exists in our database.`,
        });
      }
      if (clientData.email && emailObject[clientData.email]) {
        errors.push({
          row: i + 1,
          field: "Email",
          message: `Duplicate email, ${clientData.email} already exists in your excel.`,
        });
      }

      if (isPackageValid) {
        const servicePackage = serviceObject[clientData.package.toLowerCase()];
        const totalAmount = roundCurrency(servicePackage.totalAmount);
        const discount = roundCurrency(clientData.discount);
        const amountPaid = roundCurrency(clientData.amountPaid);
        const payableAmount = roundCurrency(totalAmount - discount);

        if (!(discount >= 0 && discount <= totalAmount)) {
          errors.push({
            row: i + 1,
            field: "Discount Amount",
            message: `Invalid discount, ${discount} should be between 0 and ${totalAmount}.`,
          });
        } else if (!(amountPaid >= 0 && amountPaid <= payableAmount)) {
          errors.push({
            row: i + 1,
            field: "Amount Paid",
            message: `Invalid amount paid, ${amountPaid} should be between 0 and ${payableAmount}. Package total is ${totalAmount}, discount is ${discount}.`,
          });
        }
      }

      extractedData.push({
        adminId: clientData.adminId,
        createdAt: clientData.createdAt,
        customerId: clientData.customerId,
        name: clientData.clientName,
        familyName: clientData.familyName,
        phoneNumber: clientData.phoneNumber,
        email: clientData.email,
        dob: clientData.dob,
        gender: clientData.gender,
        occupation: clientData.occupation,
        status: "Active",
        anniversary: clientData.anniversary,
        address: clientData.address,
        leadFrom: clientData.leadFrom,
        preference: clientData.preference,
        referredBy: clientData.referredBy,
        age: clientData?.age,
        mailLink: getUserLink(clientData?.adminId, clientData.customerId),
        services: [
          {
            purchasedAt: clientData.createdAt,
            serviceId: serviceObject[clientData.package.toLowerCase()].id,
            totalAmount:
              serviceObject[clientData.package.toLowerCase()].totalAmount,
            discount: clientData.discount ?? 0,
            amountPaid: clientData.amountPaid,
            serviceInstance: clientData.serviceInstance,
            serviceName: clientData.package,
            paymentMode: clientData.paymentMode,
            startDate: clientData.startDate,
            completionDate: clientData.completionDate,
            id: 1,
          },
        ],
      });
    } catch (validationError) {
      if (validationError.details) {
        const rowErrors = validationError.details.map((error) => {
          const fieldName = error.context.label;
          const fieldValue = error.context.value;

          if (error.path?.[0] === "leadFrom") {
            const message =
              typeof fieldValue === "string" && fieldValue.includes(",")
                ? `"${fieldName}" accepts only one value. Received "${fieldValue}". Please choose one of: ${getAllowedOptionValues(
                    leadOptions
                  )}.`
                : `"${fieldName}" value "${fieldValue}" is not valid. Allowed values are: ${getAllowedOptionValues(
                    leadOptions
                  )}.`;

            return {
              row: i + 1,
              field: fieldName,
              message,
            };
          }

          return {
            row: i + 1,
            field: fieldName,
            message: error.message,
          };
        });
        errors.push(...rowErrors);
      }
    }

    excelClientObject[
      `${clientData.clientName}_${clientData.phoneNumber}`?.toLowerCase()
    ] = true;
    phoneNumberObject[clientData.phoneNumber] = true;
    if (clientData.email) emailObject[clientData.email] = true;
  }

  return { extractedData, errors };
};
